/**
 * Utility functions for formatting time-related values in the dashboard
 */

/**
 * Formats a date/time into a human-readable "time ago" string
 * @param date - Date object or string to format
 * @param mobile - Whether to use mobile-optimized format (shorter)
 * @returns Human-readable time ago string (e.g., "2 hours ago", "just now")
 */
/**
 * SQLite stores timestamps in UTC format, and our database returns them as strings like '2025-10-08T04:25:16.304Z'
 * This function handles these UTC timestamps and converts them to relative time strings
 */
export const formatTimeAgo = (date: Date | string, mobile: boolean = false): string => {
  // Parse the input date string, ensuring proper UTC handling
  let then: Date;
  if (typeof date === 'string') {
    // Force parsing in UTC if the string doesn't already have a timezone indicator
    if (!date.endsWith('Z') && !date.includes('+')) {
      then = new Date(date + 'Z');
    } else {
      then = new Date(date);
    }
  } else {
    then = date;
  }

  const now = new Date();

  // Calculate the time difference in milliseconds
  const diffMs = now.getTime() - then.getTime();

  // If the difference is negative (future date), it's likely a timezone issue
  if (diffMs < 0) {
    // For very small negative differences (within a minute), just show "just now"
    if (diffMs > -60000) {
      return 'just now';
    }

    // For significant negative differences, log a warning and return a placeholder
    return 'recently';
  }

  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  // Format based on how long ago it was
  if (mobile) {
    // Mobile-optimized short format
    if (diffSecs < 30) return 'now';
    if (diffSecs < 60) return '<1m';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    if (diffWeeks < 4) return `${diffWeeks}w`;
    return `${diffMonths}mo`;
  }

  // Desktop format
  if (diffSecs < 30) return 'just now';
  if (diffSecs < 60) return '<1m ago';
  if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks !== 1 ? 's' : ''} ago`;
  return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;
};

/**
 * Formats duration in seconds to MM:SS format
 * @param seconds - Duration in seconds
 * @returns Formatted duration string (e.g., "3:45", "12:03")
 */
export const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};
